import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ReferrerPartner,
  ReferrerPartnerList,
  ReferrerSubmitterList,
  createReferrerPartnerBodySchema,
  updateReferrerPartnerBodySchema,
} from '@brimo/shared';
import type { z } from 'zod';
import { api, apiUpload } from '../../../../lib/api';

/**
 * Settings „Tippgeber\" (docs/dev/api-referrer-partners.md „Settings\", D-248): the organization's referrer partners
 * with their standing intake link. Queries live under `['organization', 'referrer-partners']`, which the realtime
 * event `organization.updated` refreshes with the rest of the organization settings.
 */

/** Request bodies as sent (the shared schemas' input side; the server applies their defaults and transforms). */
export type CreateReferrerPartnerInput = z.input<typeof createReferrerPartnerBodySchema>;
export type UpdateReferrerPartnerInput = z.input<typeof updateReferrerPartnerBodySchema>;

const base = '/settings/organization/referrer-partners';

export const referrerPartnerKeys = {
  all: ['organization', 'referrer-partners'] as const,
  list: () => [...referrerPartnerKeys.all, 'list'] as const,
};

export function useReferrerPartners() {
  return useQuery({
    queryKey: referrerPartnerKeys.list(),
    queryFn: () => api<ReferrerPartnerList>(base),
    select: (data) => data.items,
  });
}

/** „Einreicher anzeigen" (owner decision 02.11.2026): the people who submitted through the partner's link. */
export function useReferrerSubmitters(partnerId: string | null) {
  return useQuery({
    queryKey: [...referrerPartnerKeys.all, 'submitters', partnerId ?? ''] as const,
    queryFn: ({ signal }) => api<ReferrerSubmitterList>(`${base}/${partnerId ?? ''}/submitters`, { signal }),
    select: (data) => data.items,
    enabled: partnerId !== null,
  });
}

/** Replaces one partner in the cached list (or appends a new one) and refreshes the list in the background. */
function useStorePartner() {
  const queryClient = useQueryClient();
  return (partner: ReferrerPartner) => {
    queryClient.setQueryData<ReferrerPartnerList>(referrerPartnerKeys.list(), (current) => {
      if (!current) return current;
      const exists = current.items.some((item) => item.id === partner.id);
      return {
        items: exists
          ? current.items.map((item) => (item.id === partner.id ? partner : item))
          : [...current.items, partner],
      };
    });
    void queryClient.invalidateQueries({ queryKey: referrerPartnerKeys.all });
  };
}

export function useCreateReferrerPartner() {
  const store = useStorePartner();
  return useMutation({
    mutationFn: (body: CreateReferrerPartnerInput) => api<ReferrerPartner>(base, { method: 'POST', body }),
    onSuccess: store,
  });
}

export function useUpdateReferrerPartner() {
  const store = useStorePartner();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateReferrerPartnerInput }) =>
      api<ReferrerPartner>(`${base}/${id}`, { method: 'PATCH', body }),
    onSuccess: store,
  });
}

export function useRotateReferrerLink() {
  const store = useStorePartner();
  return useMutation({
    mutationFn: (id: string) => api<ReferrerPartner>(`${base}/${id}/rotate-link`, { method: 'POST' }),
    onSuccess: store,
  });
}

export function useDeleteReferrerPartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<null>(`${base}/${id}`, { method: 'DELETE' }),
    onSuccess: (_result, id) => {
      queryClient.setQueryData<ReferrerPartnerList>(referrerPartnerKeys.list(), (current) =>
        current ? { items: current.items.filter((item) => item.id !== id) } : current,
      );
      void queryClient.invalidateQueries({ queryKey: referrerPartnerKeys.all });
    },
  });
}

export function useUploadReferrerLogo() {
  const store = useStorePartner();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return apiUpload<ReferrerPartner>(`${base}/${id}/logo`, form);
    },
    onSuccess: store,
  });
}

export function useRemoveReferrerLogo() {
  const store = useStorePartner();
  return useMutation({
    mutationFn: (id: string) => api<ReferrerPartner>(`${base}/${id}/logo`, { method: 'DELETE' }),
    onSuccess: store,
  });
}
